// src/game/Client.js

import { onMount } from "svelte";
import { writable, get } from "svelte/store";
import { overlayStore } from "./ui/overlayStore.js";
import { getPlayerColor } from "./playerColors.js";
import { markPlayersYou, parseRoomChatLine } from "./roomChat.js";
import { parsePartyChatLine } from "./partyState.js";

const GAME_CLIENT = writable(null);

function createClient(renderer, characterCreator, muxStore) {
  let ws;
  let messageHandlers = new Map();

  let mux = muxStore;

  let activeRoom = {};
  let currentCharacter = {};
  let authToken = "";

  const getAuthToken = () => {
    if (authToken) return authToken;
    try {
      const guest = sessionStorage.getItem("talesmud_guest_token");
      if (guest) return guest;
    } catch (e) { /* ignore */ }
    try {
      const stored = localStorage.getItem("token");
      if (stored) return stored;
    } catch (e) { /* ignore */ }
    return "";
  };


  messageHandlers["enterRoom"] = (msg) => {
    const prevRoomId = activeRoom && activeRoom.id;
    activeRoom = msg.room;
    const roomChanged = !prevRoomId || !activeRoom || prevRoomId !== activeRoom.id;
    renderer(msg.message);
    overlayStore.clearAll();

    // Debug: log raw room data from server
    console.log("=== enterRoom message received ===");
    console.log("Full room object:", JSON.stringify(activeRoom, null, 2));
    console.log("Raw exits from server:", JSON.stringify(activeRoom.exits, null, 2));

    if (mux) {
      mux.setExits(activeRoom.exits);
      mux.setRoomInfo(activeRoom.name, activeRoom.description);

      //TODO: set default?
      if (activeRoom.meta != undefined && activeRoom.meta.background != undefined){
        mux.setBackground(activeRoom.meta.background)
      }

      if (activeRoom.actions != undefined) {
        mux.setActions(activeRoom.actions);
      } else {
        mux.setActions([]);
      }

      // Set NPCs in the room for UI rendering
      if (msg.npcs != undefined) {
        mux.setNPCs(msg.npcs);
      } else {
        mux.setNPCs([]);
      }

      // Set player characters in the room for UI rendering
      mux.setPlayers(markPlayersYou(msg.players || [], currentCharacter));
      if (roomChanged && mux.clearRoomChat) mux.clearRoomChat();

      // Set ground items from server-resolved item details
      mux.setGroundItems(msg.items || []);

      // Reconnect / re-select re-sends enterRoom for the SAME room — do not
      // tear down Talk/Shop overlays on that ambient refresh (~1s flap).
      if (roomChanged) {
        mux.clearDialog();
        if (mux.clearShop) mux.clearShop();
        if (mux.clearRecipes) mux.clearRecipes();
      }

      // Track room visit for minimap fallback and refresh the atlas
      mux.trackRoomVisit(activeRoom);
      requestAtlas();
    }
  };

  // Silent room update: refresh exits/items/NPCs without re-rendering the room description.
  // Used by revealExit so narrative messages aren't buried by a full room re-display.
  messageHandlers["roomUpdate"] = (msg) => {
    activeRoom = msg.room;

    // Ambient NPC/room ticks — never clear dialog/shop; never refetch atlas
    // (atlas only changes on enterRoom / real exit-graph moves).
    if (mux) {
      mux.setExits(activeRoom.exits);

      if (activeRoom.meta != undefined && activeRoom.meta.background != undefined){
        mux.setBackground(activeRoom.meta.background)
      }

      if (activeRoom.actions != undefined) {
        mux.setActions(activeRoom.actions);
      } else {
        mux.setActions([]);
      }

      if (msg.npcs != undefined) {
        mux.setNPCs(msg.npcs);
      } else {
        mux.setNPCs([]);
      }

      // Set player characters in the room
      mux.setPlayers(markPlayersYou(msg.players || [], currentCharacter));

      mux.setGroundItems(msg.items || []);
    }
  };

  messageHandlers["roomPresence"] = (msg) => {
    if (!mux) return;
    mux.setPlayers(markPlayersYou(msg.players || [], currentCharacter));
  };

  messageHandlers["createCharacter"] = (msg) => {
    renderer(msg.message);

    if (characterCreator) {
      characterCreator();
      //TODO: send select character
    }
  };
  messageHandlers["characterSelected"] = (msg) => {
    currentCharacter = msg.character;
    renderer(msg.message);
    if (mux && msg.character) {
      mux.setCharacter(msg.character);
      mux.updateCharacterStats({ xpForNextLevel: msg.xpForNextLevel });
      mux.setPlayers(markPlayersYou(get(mux)?.players || [], currentCharacter));
    }
  };

  messageHandlers["atlas"] = (msg) => {
    if (mux && msg.atlas) {
      mux.setAtlas(msg.atlas);
    }
  };

  messageHandlers["inventoryUpdate"] = (msg) => {
    if (mux) {
      mux.setInventory(msg.inventory, msg.equippedItems, msg.gold);
      // Keep character stats gold in sync
      mux.updateCharacterStats({ gold: msg.gold });
    }
  };

  messageHandlers["characterUpdate"] = (msg) => {
    if (mux) {
      mux.updateCharacterStats({
        currentHitPoints: msg.currentHitPoints,
        maxHitPoints: msg.maxHitPoints,
        currentMana: msg.currentMana,
        maxMana: msg.maxMana,
        xp: msg.xp,
        xpForNextLevel: msg.xpForNextLevel,
        level: msg.level,
        gold: msg.gold,
        inCombat: msg.inCombat,
        resting: !!msg.resting,
        attributes: msg.attributes,
        equippedSkills: Array.isArray(msg.equippedSkills) ? msg.equippedSkills : undefined,
        maxSkillSlots: msg.maxSkillSlots,
        unspentAttributePoints: msg.unspentAttributePoints,
        spentAttributePoints: msg.spentAttributePoints,
        attackPower: msg.attackPower,
        attackAttr: msg.attackAttr,
        weaponDamage: msg.weaponDamage,
        attackMod: msg.attackMod,
        defense: msg.defense,
        manaRegen: msg.manaRegen,
      });
    }
  };

  // Dialog message handler - renders NPC dialog with numbered options
  messageHandlers["dialog"] = (msg) => {
    let output = "";

    // Show NPC name and text
    output += `[${msg.npcName}] ${msg.npcText}\n`;

    // Show options if any
    if (msg.options && msg.options.length > 0) {
      output += "\n";
      for (const opt of msg.options) {
        output += `${opt.index}. ${opt.text}\n`;
      }
      output += "\nEnter a number to respond:";
    }

    renderer(output);

    // Update store for UI overlay
    if (mux) {
      mux.setDialog(msg.npcName, msg.npcText, msg.options || [], msg.conversationID || "");
    }
  };

  messageHandlers["friends"] = (msg) => {
    if (mux && mux.setFriends) {
      mux.setFriends(msg.friends || []);
    }
  };

  messageHandlers["party"] = (msg) => {
    if (mux && mux.setParty) {
      mux.setParty({
        inParty: !!msg.inParty,
        partyId: msg.partyId || msg.partyID || '',
        partyName: msg.partyName || '',
        leaderId: msg.leaderId || msg.leaderCharacterId || '',
        maxMembers: msg.maxMembers || 5,
        members: msg.members || [],
        following: !!msg.following,
      });
    }
  };

  messageHandlers["party_invite"] = (msg) => {
    if (mux && mux.setPartyInvite) {
      mux.setPartyInvite({
        pending: !!msg.pending,
        inviterName: msg.inviterName || '',
        partyId: msg.partyId || msg.partyID || '',
        expiresAt: msg.expiresAt || 0,
      });
    }
  };

  messageHandlers["shop"] = (msg) => {
    if (mux) {
      mux.setShop({
        merchantName: msg.merchantName || "",
        merchantId: msg.merchantId || "",
        gold: msg.gold || 0,
        stock: msg.stock || [],
        acceptedTypes: msg.acceptedTypes || [],
        rejectedTags: msg.rejectedTags || [],
        sellMultiplier: msg.sellMultiplier || 0.5,
      });
      if (mux.clearDialog) mux.clearDialog();
    }
    if (msg.message) {
      renderer(msg.message);
    }
  };

  messageHandlers["recipes"] = (msg) => {
    if (mux && mux.setRecipes) {
      mux.setRecipes({
        recipes: msg.recipes || [],
        roomTags: msg.roomTags || [],
      });
    }
    if (msg.message) {
      renderer(msg.message);
    }
  };

  // Dialog end message handler
  messageHandlers["dialogEnd"] = (msg) => {
    let output = "";
    if (msg.username) {
      output += `[${msg.username}] `;
    }
    output += msg.message;
    output += "\n[The conversation has ended]";
    renderer(output);
    overlayStore.pushMessage(output);

    // Clear dialog state in store
    if (mux) {
      mux.clearDialog();
    }
  };

  // Combat message handlers — BattleStage is primary UX (C2); skip room toasts.
  messageHandlers["combatStart"] = (msg) => {
    renderer(msg.message);
    if (overlayStore.clearAll) overlayStore.clearAll();
    if (mux) {
      mux.updateCharacterStats({ inCombat: true });
      if (mux.beginCombat) {
        mux.beginCombat(msg.enemies || [], msg.players || [], msg.message, msg.targetId);
      } else {
        mux.setGameContext({ inCombat: true });
        mux.setCombatants(msg.enemies || [], msg.players || []);
      }
    }
  };

  messageHandlers["combatTurn"] = (msg) => {
    renderer(msg.message);
    if (mux) {
      if (mux.setCombatTurn) {
        mux.setCombatTurn({
          actorId: msg.actorId || "",
          actorName: msg.actorName || "",
          round: msg.round || 0,
          deadlineMs: msg.deadlineMs || 0,
          queuedAction: msg.queuedAction || "",
          queuedSkillId: msg.queuedSkillId || "",
          queuedTargetId: msg.queuedTargetId || "",
          skillCooldowns: msg.skillCooldowns || {},
          nextActionAtMs: msg.nextActionAtMs || 0,
          decisionDeadlineMs: msg.decisionDeadlineMs || msg.deadlineMs || 0,
          message: msg.message,
        });
      } else {
        mux.setGameContext({ inCombat: true });
      }
    }
  };

  messageHandlers["combatAction"] = (msg) => {
    renderer(msg.message);
    if (mux && mux.applyCombatAction) {
      const priorTarget = msg.action === 'focus' ? get(mux).combatTargetId : null;
      mux.applyCombatAction({
        actorId: msg.actorId || "",
        actorName: msg.actorName || "",
        targetId: msg.targetId || "",
        action: msg.action || "",
        result: msg.result || "",
        damage: msg.damage || 0,
        heal: msg.heal || 0,
        ability: msg.ability || "",
        remainingHp: msg.remainingHp,
        maxHp: msg.maxHp,
        fxId: msg.fxId || "",
        combatants: msg.combatants || [],
        queuedAction: msg.queuedAction,
        queuedSkillId: msg.queuedSkillId,
        queuedTargetId: msg.queuedTargetId,
        skillCooldowns: msg.skillCooldowns,
        nextActionAtMs: msg.nextActionAtMs,
        decisionDeadlineMs: msg.decisionDeadlineMs,
        message: msg.message,
      });
      if (msg.action === 'focus' && priorTarget !== get(mux).combatTargetId &&
          get(mux).combatThreatWarning && typeof document !== 'undefined' &&
          !document.querySelector('.battle-stage')) {
        overlayStore.pushMessage(get(mux).combatThreatWarning.text);
      }
    }
  };

  messageHandlers["combatStatus"] = (msg) => {
    // Queue/CD snapshot for BattleStage — keep out of room toasts.
    if (mux && mux.applyCombatStatus) {
      mux.applyCombatStatus({
        queuedAction: msg.queuedAction || "",
        queuedSkillId: msg.queuedSkillId || "",
        queuedTargetId: msg.queuedTargetId || "",
        skillCooldowns: msg.skillCooldowns || {},
        nextActionAtMs: msg.nextActionAtMs || 0,
        decisionDeadlineMs: msg.decisionDeadlineMs || 0,
        round: msg.round || 0,
        combatants: msg.combatants || [],
      });
    } else if (mux) {
      mux.setGameContext({ inCombat: true });
    }
    if (msg.message) renderer(msg.message);
  };

  messageHandlers["combatEnd"] = (msg) => {
    renderer(msg.message);
    if (mux) {
      if (mux.endCombat) {
        mux.endCombat(msg.outcome || "victory", msg.message, {
          ...(msg.rewards || {}),
          loot: Array.isArray(msg.loot) ? msg.loot : [],
          levelUp: msg.levelUp || null,
          defeat: msg.defeat || null,
        });
      } else {
        mux.setGameContext({ inCombat: false });
        mux.updateCharacterStats({ inCombat: false });
        mux.setCombatants([], []);
      }
    }
  };

  // Explicit leave (for example a fled participant) has no outcome to dismiss.
  messageHandlers["combatLeave"] = (msg) => {
    if (msg.message) renderer(msg.message);
    if (mux?.clearCombat) mux.clearCombat();
  };

  // Level-up celebration — structured card (not RoomTextOverlay ASCII).
  messageHandlers["levelUp"] = (msg) => {
    if (msg.message) renderer(msg.message);
    if (!mux) return;
    // Seed HUD immediately so spend buttons work before characterUpdate.
    const seed = {};
    if (msg.newLevel) seed.level = msg.newLevel;
    if (msg.maxHitPoints) seed.maxHitPoints = msg.maxHitPoints;
    if (msg.maxHitPoints) seed.currentHitPoints = msg.maxHitPoints;
    if (msg.maxMana) seed.maxMana = msg.maxMana;
    if (msg.maxMana) seed.currentMana = msg.maxMana;
    if (msg.unspentAttributePoints != null) {
      seed.unspentAttributePoints = msg.unspentAttributePoints;
    }
    if (Object.keys(seed).length && mux.updateCharacterStats) {
      mux.updateCharacterStats(seed);
    }
    if (!mux.addQuestNotification) return;
    const gains = msg.attributeGains && typeof msg.attributeGains === 'object'
      ? msg.attributeGains
      : {};
    mux.addQuestNotification({
      id: `level-up-${msg.newLevel || Date.now()}-${Date.now()}`,
      type: 'levelup',
      questId: null,
      questName: msg.newLevel ? `Level ${msg.newLevel}` : 'Level Up',
      message: msg.message || '',
      oldLevel: msg.oldLevel || 0,
      newLevel: msg.newLevel || 0,
      levelsGained: msg.levelsGained || 0,
      hpGained: msg.hpGained || 0,
      manaGained: msg.manaGained || 0,
      attributeGains: gains,
      attributePointsGained: msg.attributePointsGained || 0,
      unspentAttributePoints: msg.unspentAttributePoints || 0,
      maxHitPoints: msg.maxHitPoints || 0,
      maxMana: msg.maxMana || 0,
    });
  };

  // Quest message handlers
  messageHandlers["questAccepted"] = (msg) => {
    renderer(msg.message);
    // Do not push to RoomTextOverlay — accepted uses the large centered QuestNotifications card.

    // Update quest log
    requestQuestLog();

    // Show notification + refresh open Talk options immediately
    if (mux) {
      mux.addQuestNotification({
        id: `quest-accepted-${msg.questId || Date.now()}`,
        questId: msg.questId,
        type: 'accepted',
        questName: msg.questName || 'Quest',
        message: msg.message || 'Quest accepted',
        objectives: msg.objectives || [],
      });
      if (mux.markDialogQuestAccepted) {
        mux.markDialogQuestAccepted(msg.questName);
      }
    }
  };

  messageHandlers["questProgress"] = (msg) => {
    renderer(msg.message);

    // Update quest log
    requestQuestLog();

    // Show objective progress notification
    if (mux) {
      const changedObjective = msg.changedObjective || (msg.objectives || []).find(o => o.completed);
      if (changedObjective) {
        const progressText = changedObjective.completed
          ? `Objective complete: ${changedObjective.description || 'Objective completed'}`
          : `${changedObjective.description || 'Objective'}: ${changedObjective.current}/${changedObjective.required}`;
        mux.addQuestNotification({
          id: `quest-progress-${msg.questId || Date.now()}-${changedObjective.objectiveId}-${Date.now()}`,
          questId: msg.questId,
          type: 'progress',
          questName: msg.questName || 'Quest',
          message: progressText,
        });
      }
    }
  };

  messageHandlers["questReady"] = (msg) => {
    renderer(msg.message);
    overlayStore.pushMessage(msg.message);

    requestQuestLog();

    if (mux) {
      const changedObjective = msg.changedObjective || (msg.objectives || []).find(o => o.completed);
      mux.addQuestNotification({
        id: `quest-ready-${msg.questId || Date.now()}`,
        questId: msg.questId,
        type: 'ready',
        questName: msg.questName || 'Quest',
        message: changedObjective?.description
          ? `Ready to turn in. Last objective: ${changedObjective.description}`
          : 'Ready to turn in.',
      });
    }
  };

  messageHandlers["questCompleted"] = (msg) => {
    renderer(msg.message);
    // Do not push ASCII/reward blobs to RoomTextOverlay — completed uses the Veilspan card.

    requestQuestLog();

    if (mux) {
      mux.addQuestNotification({
        id: `quest-completed-${msg.questId || Date.now()}`,
        questId: msg.questId,
        type: 'completed',
        questName: msg.questName || 'Quest',
        message: msg.message || 'Quest complete',
        objectives: msg.objectives || [],
      });
    }
  };

  messageHandlers["questAbandoned"] = (msg) => {
    renderer(msg.message);

    // Refresh quest log so the abandoned quest is removed from the active list
    requestQuestLog();
  };

  messageHandlers["questLog"] = (msg) => {
    // Update full quest log
    if (mux && msg.quests) {
      mux.updateQuests(msg.quests);
    }
  };

  const requestAtlas = () => {
    if (!currentCharacter || !currentCharacter.id) return;
    const token = getAuthToken();
    if (!token) return;

    fetch(`/api/characters/${currentCharacter.id}/map`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })
    .then(r => {
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json();
    })
    .then(data => {
      if (mux && data) {
        mux.setAtlas(data);
      }
    })
    .catch((err) => { console.warn("atlas fetch failed", err); });
  };

  // Helper function to fetch quest log from API
  const requestQuestLog = () => {
    if (!currentCharacter || !currentCharacter.id) return;

    // Guests keep their JWT in sessionStorage (talesmud_guest_token), not localStorage.
    // Use the same resolver as atlas/map fetches so quest-log refresh works for both.
    const token = getAuthToken();
    if (!token) return;

    fetch(`/api/quest-progress/${currentCharacter.id}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })
    .then(r => {
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json();
    })
    .then(data => {
      if (mux && Array.isArray(data)) {
        mux.updateQuests(data);
      }
    })
    .catch(err => {
      console.error('Failed to fetch quest log:', err);
    });
  };

  let boundSocket = null;
  let onMessageHandler = null;
  let onCloseHandler = null;

  const setWSClient = async (wscl) => {
    // Avoid stacking duplicate message/close listeners across reconnects.
    if (boundSocket && boundSocket !== wscl && onMessageHandler) {
      try { boundSocket.removeEventListener("message", onMessageHandler); } catch (e) { /* ignore */ }
      try { boundSocket.removeEventListener("close", onCloseHandler); } catch (e) { /* ignore */ }
    }
    ws = wscl;
    if (!wscl || boundSocket === wscl) return;
    boundSocket = wscl;
    updateClient(wscl);
  };

  const updateClient = (socket) => {
    onMessageHandler = function (e) {
      var msg = JSON.parse(e.data);

      if (messageHandlers[msg.type]) {
        messageHandlers[msg.type](msg);
      } else {
        let message = msg.message;

        if (message === "" || message === "\n") {
          console.log("RECEIVED EMPTY MESSAGE")
          return;
        }

        if (msg.username) {
          // Pass structured data so terminals can color the player name
          const color = getPlayerColor(msg.username);
          renderer({ username: msg.username, message: msg.message, color });
          // Room mood / system ambiance: body only on overlay (no SYSTEM: prefix).
          // Terminal still gets the username via renderer above.
          if (/^system$/i.test(String(msg.username))) {
            overlayStore.pushMessage({ text: msg.message, kind: 'ambiance' });
          } else {
            overlayStore.pushMessage(msg.username + ":  " + msg.message);
          }
        } else {
          renderer(message);
          overlayStore.pushMessage(message);
        }

        if (mux && mux.appendPartyChat) {
          const raw = msg && (msg.message || '');
          if (typeof raw === 'string' && raw.indexOf('[Party]') === 0) {
            const pline = parsePartyChatLine(raw, currentCharacter && currentCharacter.name);
            if (pline) mux.appendPartyChat(pline);
          }
        }

        if (mux && mux.appendRoomChat) {
          const line = parseRoomChatLine(msg, currentCharacter);
          if (line) mux.appendRoomChat(line);
        }

        if (msg.style === "combatEvent" && mux && mux.appendCombatEvent) {
          mux.appendCombatEvent({
            text: msg.message,
            hook: msg.hook || "",
            source: msg.source || "",
          });
        }

        if (!msg.username) {
          if (mux && get(mux)?.shop && mux.setShopError) {
            const lower = String(message || "").toLowerCase();
            if (
              lower.includes("gold") ||
              lower.includes("stock") ||
              lower.includes("doesn't") ||
              lower.includes("cannot") ||
              lower.includes("can't") ||
              lower.includes("enough") ||
              lower.includes("full") ||
              lower.includes("don't have") ||
              lower.includes("level")
            ) {
              mux.setShopError(message);
            }
          }
          if (mux && get(mux)?.recipes && mux.setCraftError) {
            const lower = String(message || "").toLowerCase();
            if (lower.includes("you craft")) {
              // Success — clear prior craft errors; counts refresh via inventoryUpdate.
              if (mux.clearCraftError) mux.clearCraftError();
            } else if (
              lower.includes("missing materials") ||
              lower.includes("can't craft") ||
              lower.includes("cannot craft") ||
              lower.includes("unknown recipe") ||
              lower.includes("inventory is full") ||
              lower.includes("requires a") ||
              lower.includes("something went wrong")
            ) {
              mux.setCraftError(message);
            }
          }
        }
      }
    };

    onCloseHandler = function (e) {
      const code = e && typeof e.code === "number" ? e.code : 0;
      const reason = (e && e.reason) || "";
      console.info("[ws-client] close", { code, reason });
      // Game.svelte owns reconnect; only surface a terminal note for real drops.
      if (code !== 4001) {
        renderer("Connection Closed.");
      }
    };

    socket.addEventListener("message", onMessageHandler);
    socket.addEventListener("close", onCloseHandler);
  };

  const onInput = async (data) => {
    const msg = await handleInput(data);
    sendMessage(msg);
    //renderer(msg);
  };

  const sendMessage = (msg) => {
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      // Status ownership stays in Game.svelte open/close handlers — do not
      // flap connectionStatus yellow on every failed send.
      renderer("Connection is down. Wait for reconnect, then try again.");
      return;
    }

    ws.send(
      JSON.stringify({
        message: msg,
        type: "message",
      })
    );
  };

  const renderRoom = async (room) => {
    renderer(room.description);
  };

  const handleInput = async (data) => {
    // `spells` is a silent legacy alias for the skills command. Players see Skills.
    const text = `${data ?? ""}`;
    return text.replace(/^(\s*)spells\b/i, "$1skills");
  };

  const client = {
    onInput,
    setWSClient,
    sendMessage,
    setAuthToken: (token) => { authToken = token || ""; },
  };

  // Set the client object in the store
  GAME_CLIENT.set(client);

  return client;
}

function getClient() {
  // Return the client object from the store
  return get(GAME_CLIENT);
}

export { createClient, getClient };
